import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { PAYMENT_METHOD_STATUS } from "@/utils/InvoicePay";

import { getErrorMessage } from "../utils/getErrorMessage";
import {
  createPaymentMethodApi,
  listPaymentMethodsApi
} from "../utils/PaymentsService/PaymentMethodApis";

interface UseStripePaymentSetupProps {
  clientSecret: string | null;
  paymentMethodId: string;
  processing: boolean;
  error: string | null;
}

export const useStripePaymentSetup = (
  paymentProvider: string
): UseStripePaymentSetupProps => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentMethodId, setPaymentMethodId] = useState<string>("");
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClientSecret = async () => {
      const listPaymentResponse = await listPaymentMethodsApi({
        status: PAYMENT_METHOD_STATUS.PENDING
      });
      if (
        listPaymentResponse.status === 200 &&
        listPaymentResponse?.data?.paymentMethodsList?.length
      ) {
        const result = listPaymentResponse.data.paymentMethodsList?.[0];
        setClientSecret(result?.paymentMethodLifecycleDetails?.clientSecret);
        setPaymentMethodId(result?.paymentMethodId);
      } else {
        try {
          setProcessing(true);
          const response = await createPaymentMethodApi({
            paymentProvider,
            idempotencyToken: uuidv4()
          });

          if (response?.status === 200) {
            setClientSecret(
              response.data?.paymentMethodLifecycleDetails?.clientSecret || null
            );
            setPaymentMethodId(response.data?.paymentMethodId);
          } else {
            throw new Error("Failed to create payment method");
          }
        } catch (err: unknown) {
          let message = "Error";
          if (err instanceof AxiosError && err.response?.data?.errorCode) {
            message = getErrorMessage(err.response.data.errorCode);
          }
          setError(message);
        } finally {
          setProcessing(false);
        }
      }
    };

    fetchClientSecret();
  }, [paymentProvider]);

  return {
    clientSecret,
    paymentMethodId,
    processing,
    error
  };
};
